import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import './App.css';
import Footer from './components/Footer';

// Lazy load components
const Home = lazy(() => import('./pages/Home'));
const JsonPrettyViewer = lazy(() => import('./pages/JsonPrettyViewer'));
const Placeholder = lazy(() => import('./pages/Placeholder'));
const JsonComparator = lazy(() => import('./pages/JsonComparator'));
const JsonToCsvConverter = lazy(() => import('./pages/Json2CSVConverter'));
const CsvToJsonConverter = lazy(() => import('./pages/CSV2JsonConverter'));
const XML2Json = lazy(() => import('./pages/XML2Json'));
const YamlToJsonConverter = lazy(() => import('./pages/YAML2JsonConverter'));
const JsonToXmlConverter = lazy(() => import('./pages/Json2XML'));
const JsonToYamlConverter = lazy(() => import('./pages/Json2YAML'));
const YamlViewer = lazy(() => import('./pages/YamlViewer'));
const YamlFormatter = lazy(() => import('./pages/YamlFormatter')); // Add YAML Formatter
const JsonPathQuery = lazy(() => import('./pages/JsonPathQuery'));
const JsonSchemaGenerator = lazy(() => import('./pages/JsonSchemaGenerator'));
const JsonToTsInterface = lazy(() => import('./pages/Json2TSInterface'));
const JsonFlattenUnflatten = lazy(() => import('./pages/JsonFlattenUnflatten'));
const JsonEscapeUnescape = lazy(() => import('./pages/JsonEscapeUnescape'));
const JsonHierarchyKeyExtractor = lazy(() => import('./pages/JsonHierarchyKeyExtractor'));
const AutoRandomLargeJsonGenerator = lazy(() => import('./pages/RandomJsonGenerator'));
const JsonSizeCalculator = lazy(() => import('./pages/JsonSizeCalculator'));
const JsonToMarkdownTable = lazy(() => import('./pages/JsonToMarkdownTable'));
const JwtEncoderDecoder = lazy(() => import('./pages/JwtEncoderDecoder'));
const EpochConverter = lazy(() => import('./pages/EpochConverter'));
const JwtDecoder = lazy(() => import('./pages/JwtDecoder'));
const JsonFormatter = lazy(() => import('./pages/JsonFormatter'));
const JsonMinifier = lazy(() => import('./pages/JsonMinifier'));
const JsonValidator = lazy(() => import('./pages/JsonValidator'));
const CrontabGenerator = lazy(() => import('./pages/CrontabGenerator'));
const TerraformGenerator = lazy(() => import('./pages/TerraformGenerator'));
const GitHubActionsBuilder = lazy(() => import('./pages/GitHubActionsBuilder'));
const JenkinsPipelineGenerator = lazy(() => import('./pages/JenkinsPipelineGenerator'));
const OpenApiPreviewer = lazy(() => import('./pages/OpenApiPreviewer'));
const Json2OpenAPI = lazy(() => import('./pages/Json2OpenAPI'));
const LogViewer = lazy(() => import('./pages/LogViewer'));
const EncoderDecoder = lazy(() => import('./pages/EncoderDecoder'));
const Hashing = lazy(() => import('./pages/Hashing'));
const EncryptionDecryption = lazy(() => import('./pages/EncryptionDecryption'));
const TextComparator = lazy(() => import('./pages/TextComparator'));
const RegexTester = lazy(() => import('./pages/RegexTester'));
const CssFormatter = lazy(() => import('./pages/CssFormatter'));
const CssLinter = lazy(() => import('./pages/CssLinter'));
const JavaScriptFormatter = lazy(() => import('./pages/JavaScriptFormatter'));
const JavaScriptLinter = lazy(() => import('./pages/JavaScriptLinter'));
const XmlFormatter = lazy(() => import('./pages/XmlFormatter'));
const SqlFormatter = lazy(() => import('./pages/SqlFormatter'));
const MarkdownPreviewer = lazy(() => import('./pages/MarkdownPreviewer'));
const HarViewer = lazy(() => import('./pages/HarViewer'));
const DockerfileValidator = lazy(() => import('./pages/DockerfileValidator'));
const GeneralConverters = lazy(() => import('./pages/GeneralConverters'));
const NetworkTools = lazy(() => import('./pages/NetworkTools'));
const CORSTester = lazy(() => import('./pages/CORSTester'));
const RestClient = lazy(() => import('./pages/RestClient'));
const NoSQLExplorer = lazy(() => import('./pages/NoSQLExplorer'));
const ImageCompression = lazy(() => import('./pages/ImageCompression'));
const ImageDimensionChanger = lazy(() => import('./pages/ImageDimensionChanger'));
const ImageTextExtractor = lazy(() => import('./pages/ImageTextExtractor'));

// Tool Suggestion component
const ToolSuggestion = lazy(() => import('./pages/ToolSuggestion'));

// Cheat Sheets component
const CheatSheets = lazy(() => import('./pages/CheatSheets'));

// 404 Not Found Page
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

// Landing Pages for SEO
const JsonFormatterLanding = lazy(() => import('./pages/JsonFormatterLanding'));
const BlogPage = lazy(() => import('./pages/BlogPage'));

// Blog Posts
const JsonFormattingGuide = lazy(() => import('./blogposts/JsonFormattingGuide'));
const RestApiTestingGuide = lazy(() => import('./blogposts/RestApiTestingGuide'));
const RegexTutorialGuide = lazy(() => import('./blogposts/RegexTutorialGuide'));
const JwtGuide = lazy(() => import('./blogposts/JwtGuide'));
const WebSecurityGuide = lazy(() => import('./blogposts/WebSecurityGuide'));
const CodeFormattingGuide = lazy(() => import('./blogposts/CodeFormattingGuide'));
const DataConversionGuide = lazy(() => import('./blogposts/DataConversionGuide'));

// Linter components
const PythonLinter = lazy(() => import('./pages/PythonLinter'));
const JavaLinter = lazy(() => import('./pages/JavaLinter'));
const GoLinter = lazy(() => import('./pages/GoLinter'));
const RustLinter = lazy(() => import('./pages/RustLinter'));
const SwiftLinter = lazy(() => import('./pages/SwiftLinter'));
const TypeScriptLinter = lazy(() => import('./pages/TypeScriptLinter'));
const RubyLinter = lazy(() => import('./pages/RubyLinter'));
const HtmlLinter = lazy(() => import('./pages/HtmlLinter'));

// MultiLanguageLinter component
const MultiLanguageLinter = lazy(() => import('./pages/MultiLanguageLinter'));

// Loading component
const Loading = () => (
  <div className="loading-container">
    <div className="loading-spinner"></div>
    <p>Loading...</p>
  </div>
);

function App() {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Check local storage or system preference (Defaulting to dark)
    if (localStorage.theme === 'light') return true;
    if (localStorage.theme === 'dark') return false;

    // Default to dark mode if no preference is set
    return true;
  });
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.theme = 'dark';
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.theme = 'light';
    }
  }, [isDarkMode]);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  return (
    <div className="relative flex min-h-screen w-full flex-col bg-background-light dark:bg-background-dark text-slate-900 dark:text-slate-100 font-display transition-colors duration-200">
      <header className="sticky top-0 z-50 w-full border-b border-brand/10 dark:border-[#2a2b4a] bg-white/80 dark:bg-[#161832]/95 backdrop-blur-md px-6 md:px-20 lg:px-40 py-3">
        <div className="flex items-center justify-between">
          <Link to="/home" className="flex items-center gap-2 text-brand no-underline hover:opacity-80 transition-opacity">
            <span className="material-symbols-outlined text-3xl font-bold">terminal</span>
            <h2 className="text-slate-900 dark:text-white text-xl font-black tracking-tight mb-0">DevToolStatio</h2>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <nav className="flex items-center gap-6 border-r border-brand/10 dark:border-[#2a2b4a] pr-6 mr-1">
              <a href="/home#tools-directory" onClick={(e) => {
                if (window.location.pathname === '/home') {
                  e.preventDefault();
                  document.getElementById('tools-directory')?.scrollIntoView({ behavior: 'smooth' });
                  window.history.pushState(null, '', '#tools-directory');
                }
              }} className="text-slate-600 dark:text-slate-300 text-sm font-semibold hover:text-brand transition-colors no-underline">Tools</a>
              <a href="/home#categories" onClick={(e) => {
                if (window.location.pathname === '/home') {
                  e.preventDefault();
                  document.getElementById('categories')?.scrollIntoView({ behavior: 'smooth' });
                  window.history.pushState(null, '', '#categories');
                }
              }} className="text-slate-600 dark:text-slate-300 text-sm font-semibold hover:text-brand transition-colors no-underline">Categories</a>
              <Link to="/blog" className="text-slate-600 dark:text-slate-300 text-sm font-semibold hover:text-brand transition-colors no-underline">Docs</Link>
            </nav>
            <button
              onClick={toggleTheme}
              className="w-9 h-9 rounded-full flex items-center justify-center text-slate-600 dark:text-slate-300 hover:bg-brand/10 transition-colors border border-brand/10 dark:border-[#2a2b4a] bg-transparent p-0 m-0 shadow-none hover:shadow-none"
            >
              <span className="material-symbols-outlined text-xl">{isDarkMode ? 'light_mode' : 'dark_mode'}</span>
            </button>
          </div>
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-slate-900 dark:text-white bg-transparent p-0 m-0 shadow-none"
          >
            <span className="material-symbols-outlined">{isMobileMenuOpen ? 'close' : 'menu'}</span>
          </button>
        </div>

        {/* Mobile Navigation Drawer */}
        <div className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out ${isMobileMenuOpen ? 'max-h-60 opacity-100' : 'max-h-0 opacity-0'}`}>
          <nav className="flex flex-col gap-1 py-3 border-t border-brand/10 dark:border-[#2a2b4a]">
            <a href="/home#tools-directory" onClick={(e) => {
              if (window.location.pathname === '/home') {
                e.preventDefault();
                document.getElementById('tools-directory')?.scrollIntoView({ behavior: 'smooth' });
                window.history.pushState(null, '', '#tools-directory');
              }
              setIsMobileMenuOpen(false);
            }} className="px-3 py-2.5 rounded-xl text-slate-700 dark:text-slate-300 text-sm font-semibold hover:bg-brand/10 transition-colors no-underline flex items-center gap-3">
              <span className="material-symbols-outlined text-brand text-lg">build</span> Tools
            </a>
            <a href="/home#categories" onClick={(e) => {
              if (window.location.pathname === '/home') {
                e.preventDefault();
                document.getElementById('categories')?.scrollIntoView({ behavior: 'smooth' });
                window.history.pushState(null, '', '#categories');
              }
              setIsMobileMenuOpen(false);
            }} className="px-3 py-2.5 rounded-xl text-slate-700 dark:text-slate-300 text-sm font-semibold hover:bg-brand/10 transition-colors no-underline flex items-center gap-3">
              <span className="material-symbols-outlined text-brand text-lg">category</span> Categories
            </a>
            <Link to="/blog" className="px-3 py-2.5 rounded-xl text-slate-700 dark:text-slate-300 text-sm font-semibold hover:bg-brand/10 transition-colors no-underline flex items-center gap-3">
              <span className="material-symbols-outlined text-brand text-lg">menu_book</span> Docs
            </Link>
            <button
              onClick={toggleTheme}
              className="px-3 py-2.5 rounded-xl text-slate-700 dark:text-slate-300 text-sm font-semibold hover:bg-brand/10 transition-colors flex items-center gap-3 w-full text-left bg-transparent border-none shadow-none m-0"
            >
              <span className="material-symbols-outlined text-brand text-lg">{isDarkMode ? 'light_mode' : 'dark_mode'}</span> {isDarkMode ? 'Light Mode' : 'Dark Mode'}
            </button>
          </nav>
        </div>
      </header>

      <main className="flex-1 flex flex-col items-stretch">
        <Suspense fallback={<Loading />}>
          <Routes>
            <Route path="/" element={<Navigate to="/home" />} />
            <Route path="/home" element={<Home />} />
            <Route path="/jsonViewer" element={<JsonPrettyViewer />} />

            {/* JSON Tools Component */}
            <Route path="/json" element={<JsonFormatter />} />

            <Route path="/jsonFormatter" element={<JsonFormatter />} />
            <Route path="/jsonMinifier" element={<JsonMinifier />} />
            <Route path="/jsonValidator" element={<JsonValidator />} />

            <Route path="/jsonComparator" element={<JsonComparator />} />
            <Route path="/json2csv" element={<JsonToCsvConverter />} />
            <Route path="/csv2json" element={<CsvToJsonConverter />} />
            <Route path="/xml2json" element={<XML2Json />} />
            <Route path="/json2xml" element={<JsonToXmlConverter />} />
            <Route path="/yaml2json" element={<YamlToJsonConverter />} />
            <Route path="/json2yaml" element={<JsonToYamlConverter />} />
            <Route path="/codeLinter" element={<MultiLanguageLinter />} />
            <Route path="/yamlViewer" element={<YamlViewer />} />
            <Route path="/yamlFormatter" element={<YamlFormatter />} />
            <Route path="/jsonPath" element={<JsonPathQuery />} />
            <Route path="/jsonSchema" element={<JsonSchemaGenerator />} />
            <Route path="/json2ts" element={<JsonToTsInterface />} />
            <Route path="/jsonFlat" element={<JsonFlattenUnflatten />} />
            <Route path="/jsonEscape" element={<JsonEscapeUnescape />} />
            <Route path="/jsonKeyExtractor" element={<JsonHierarchyKeyExtractor />} />
            <Route path="/randomJsonGenerator" element={<AutoRandomLargeJsonGenerator />} />
            <Route path="/jsonsize" element={<JsonSizeCalculator />} />
            <Route path="/json2Markdown" element={<JsonToMarkdownTable />} />
            <Route path="/jwtEncoderDecoder" element={<JwtEncoderDecoder />} />
            <Route path="/epochConverter" element={<EpochConverter />} />
            <Route path="/crontabGenerator" element={<CrontabGenerator />} />
            <Route path="/terraformGenerator" element={<TerraformGenerator />} />
            <Route path="/githubActionsBuilder" element={<GitHubActionsBuilder />} />
            <Route path="/jenkinsPipelineGenerator" element={<JenkinsPipelineGenerator />} />
            <Route path="/OpenApiPreviewer" element={<OpenApiPreviewer />} />
            <Route path="/json2OpenApi" element={<Json2OpenAPI />} />
            <Route path="/logViewer" element={<LogViewer />} />
            <Route path="/encoderDecoder" element={<EncoderDecoder />} />
            <Route path="/hashing" element={<Hashing />} />
            <Route path="/encryptionDecryption" element={<EncryptionDecryption />} />
            <Route path="/textComparator" element={<TextComparator />} />
            <Route path="/regexTester" element={<RegexTester />} />
            <Route path="/cssFormatter" element={<CssFormatter />} />
            <Route path="/javascriptFormatter" element={<JavaScriptFormatter />} />
            <Route path="/xmlFormatter" element={<XmlFormatter />} />
            <Route path="/sqlFormatter" element={<SqlFormatter />} />
            <Route path="/markdownPreviewer" element={<MarkdownPreviewer />} />
            <Route path="/harViewer" element={<HarViewer />} />
            <Route path="/dockerfileValidator" element={<DockerfileValidator />} />
            <Route path="/generalConverters" element={<GeneralConverters />} />
            <Route path="/networkTools" element={<NetworkTools />} />
            <Route path="/corsTester" element={<CORSTester />} />
            <Route path="/restClient" element={<RestClient />} />
            <Route path="/nosqlExplorer" element={<NoSQLExplorer />} />
            <Route path="/imageCompression" element={<ImageCompression />} />
            <Route path="/imageDimensionChanger" element={<ImageDimensionChanger />} />
            <Route path="/imageTextExtractor" element={<ImageTextExtractor />} />

            {/* SEO Landing Pages */}
            <Route path="/json-formatter" element={<JsonFormatterLanding />} />
            <Route path="/blog" element={<BlogPage />} />

            {/* Blog Post Routes */}
            <Route path="/blog/json-formatting-guide" element={<JsonFormattingGuide />} />
            <Route path="/blog/rest-api-testing-guide" element={<RestApiTestingGuide />} />
            <Route path="/blog/regex-tutorial-developers" element={<RegexTutorialGuide />} />
            <Route path="/blog/jwt-tokens-explained" element={<JwtGuide />} />
            <Route path="/blog/web-security-guide" element={<WebSecurityGuide />} />
            <Route path="/blog/data-conversion-guide" element={<DataConversionGuide />} />
            <Route path="/blog/css-formatting-guide" element={<CodeFormattingGuide />} />
            <Route path="/blog/javascript-formatting-guide" element={<CodeFormattingGuide />} />
            <Route path="/blog/sql-formatting-guide" element={<CodeFormattingGuide />} />

            {/* Tool Suggestion Route */}
            <Route path="/toolSuggestion" element={<ToolSuggestion />} />

            {/* Cheat Sheets Route */}
            <Route path="/cheatSheets" element={<CheatSheets />} />

            {/* 404 Not Found Route */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </main>
      <Footer className="mt-auto" />
    </div>
  );
}

export default React.memo(App);
